import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Alert } from "@mui/material";
import Select, {components} from 'react-select';
import { IoIosSearch } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { getContentTitleFilter, getAuthorEmailFilter, getEntityTypeFilter, getEntityNameFilter, 
  getDraftListFilter,getScheduledQueueList,getPublishedList } from "../../../../../store/actions/cms/scheduler";
import { clearData } from '../../../../../store/slices/scheduler';
import { findPermission } from '../../../../utils';

const FilterModal = ({ show, onHide, setCurrentPage,setQueueCurrentPage,setPublishedCurrentPage}) => {
  const MAX_VISIBLE_VALUES = 2;
  const [debounceState, setDebounceState] = useState("");
  const dispatch = useDispatch();
  const [contentType, setContentType] = useState(null);
  const [authorType, setAuthorType] = useState('all');
  const [title, setTitle] = useState(null);
  const [customAuthor, setCustomAuthor] = useState(null);
  const [entityType, setEntityType] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState(null);
  const { user } = useSelector((state) => state.userData);
  const { contentTypeData, contentTitleData, contentTitleLoading, authorEmailData, authorEmailLoading, entityTypeData, 
    entityNameData, draftListData, publishedListData, queueDraftsData, message,
  } = useSelector((state) => {return (state?.contentSchedularReducer)})
  const activeTab = localStorage.getItem('activeContentTab');

  // to get the param on the basis of selected tab for content title
  const getIdentifier = (activeTab) => {
    const params = {};
  
    switch (activeTab) {
      case 'draft':
        params.tab_name = 'draft-list';
        break;
      case 'queue':
        params.task_status = 'pending';
        break;
      case 'published':
        params.draft_status = 1;
        break;
      default:
        params.tab_name = 'unknown';
    }
    return params;
  };

  const identifier = getIdentifier(activeTab);

  useEffect(() => {
    dispatch(getContentTitleFilter({filter_type: "content_title",...identifier})); 
    dispatch(getAuthorEmailFilter({filter_type: "user_email"}));
    dispatch(getEntityTypeFilter({filter_type:"entity_type"}));
    return localStorage.removeItem("filterApplied");
  }, []);

  useEffect(() => {
    if(entityType){
      dispatch(getEntityNameFilter({filter_type:"entity_name", entity_type: entityType?.entity_type}));
    }
  }, [entityType]);

  useEffect(() => {
    if (draftListData || publishedListData || queueDraftsData) {
      onHide();
    }
  }, [draftListData,publishedListData,queueDraftsData]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => dispatch(clearData()), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if(activeTab){
      clearFilterStates();
    }
  }, [activeTab]);

  const resetToPageOne = () => {
    if(activeTab ==='draft') setCurrentPage(1);
    if(activeTab ==='queue') setQueueCurrentPage(1);
    if(activeTab ==='published') setPublishedCurrentPage(1)
  };

  // destructuring params for calling API
  const createParams = (excludeKeys = []) => {
    let params = {
      content_type: contentType?.content_type,
      content_title: title?.content_title,
      entity_type: entityType?.entity_type,
      ...(selectedEntities?.length && {
        article_entity_id: selectedEntities.map(e => e?.article_entity_id).join(','),
      }),
    };

    if (authorType === 'my_work') {
      params.user_id = user?.data?.id;
    } else if (authorType === 'user_email') {
      params.user_id = customAuthor?.user_id;
    }

    // to remove excluded keys
    excludeKeys.forEach(key => delete params[key]);

    localStorage.setItem('filterApplied', JSON.stringify(params));
    return params;
  };

  const applyFilters = () => {
    let params = createParams();
    resetToPageOne();
    // draft tab
    if(activeTab ==='draft') dispatch(getDraftListFilter({...params}));
    // in queue tab
    if(activeTab ==='queue') dispatch(getScheduledQueueList({...params}));
    // published tab
    if(activeTab ==='published') dispatch(getPublishedList(params));
  };

  // handle dependency for content title at others
  const handleFocusTitle = () => {
    dispatch(clearData());
    let params = createParams(['content_title']);
    dispatch(getContentTitleFilter({ ...params,...identifier, filter_type: 'content_title' }));
  };

  const clearFilterStates = () =>{
    setContentType(null);
    setAuthorType('all');
    setTitle(null);
    setEntityType('');
    setCustomAuthor(null);
    setSelectedEntities([]);
  }

  const clearFilters = () => {
    clearFilterStates();
    resetToPageOne();
    // draft
    if(activeTab ==='draft') dispatch(getDraftListFilter());
    // in queue
    if(activeTab ==='queue') dispatch(getScheduledQueueList());
    // published tab
    if(activeTab ==='published') dispatch(getPublishedList());

    localStorage.removeItem("filterApplied");
  };
  
  // customized entity name
  const customStyles = {
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e6eefa',
      borderRadius: '6px',
      padding: '3px 6px',
      marginRight: '6px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#0052cc', 
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#0052cc', 
    }),
    menu:(base)=>({
      ...base,
      zIndex:2
    }),
  };

  const handleInputChange = (filter_type, value) => {
    let params = {};
    params.term = value;
    params.filter_type = filter_type;
    let inDebounce = debounceState;
    if (filter_type === "content_title") {
      let restParams = createParams(['content_title']);
      clearTimeout(inDebounce);
      inDebounce = setTimeout(() => {
        dispatch(getContentTitleFilter({...params,...restParams,...identifier})); 
      }, 1000);
      setDebounceState(inDebounce);
    }
    if (filter_type === "user_email") {
      clearTimeout(inDebounce);
      inDebounce = setTimeout(() => {
        dispatch(getAuthorEmailFilter(params));
      }, 1000);
      setDebounceState(inDebounce);
    }
    if (filter_type === "entity_name") {
      clearTimeout(inDebounce);
      inDebounce = setTimeout(() => {
        params.entity_type = entityType?.entity_type;
        dispatch(getEntityNameFilter(params));
      }, 1000);
      setDebounceState(inDebounce);
    }
  };


  // customize multi select options
  const customOption = (props) => {
    const { data, innerRef, innerProps, isSelected } = props;

    return (
      <div ref={innerRef} {...innerProps} style={{ padding: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          style={{ marginRight: 8 }}
        />
        { data?.user__email || data?.content_name || data?.entity_name}
      </div>
    );
  };

// customized values for entity name
  const customMultiValue = (props) => {
    const { index, getValue } = props;
    const selected = getValue();

    if (index < MAX_VISIBLE_VALUES) {
      return <components.MultiValue {...props} />;
    }

    if (index === MAX_VISIBLE_VALUES) {
      const extraCount = selected.length - MAX_VISIBLE_VALUES;
      if (extraCount > 0) {
        return (
          <div
            style = {{
              backgroundColor: '#e6eefa',
              color: '#0052cc',
              borderRadius: '6px',
              padding: '3px 6px',
              marginRight: '6px',
            }}
          >
            +{extraCount} 
          </div>
        );
      }
    }

    return null;
  };


  return (
    <Modal show={show} onHide={onHide} id='scheduler_filter'>
      <Modal.Header closeButton>
        <Modal.Title>Filter</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Filter Form */}
        <Form.Group className="mb-3">
          <Form.Label>Filter by Content type</Form.Label>
            <Select
                value={contentType}
                onChange={(selected) => setContentType(selected)}
                options={contentTypeData?.content_types}
                getOptionLabel={(option) => option.content_name}
                getOptionValue={(option) => option.content_type}
                placeholder="Select content type"
                isClearable={true}
            />
        </Form.Group>

        <Form.Group className="mb-3">
            <Form.Label>Filter by Author</Form.Label>
            <div>
                <Form.Check type="radio" id="author-all" name="author" className="mb-2">
                <Form.Check.Input
                    type="radio"
                    name="author"
                    value="all"
                    checked={authorType === 'all'}
                    onChange={() => {
                        setAuthorType('all');
                        setCustomAuthor('');
                    }}
                />
                <Form.Check.Label htmlFor="author-all">All Authors</Form.Check.Label>
                </Form.Check>

                <Form.Check type="radio" id="author-my" name="author" className="mb-2">
                <Form.Check.Input
                    type="radio"
                    name="author"
                    value="my"
                    checked={authorType === 'my_work'}
                    onChange={() => {
                        setAuthorType('my_work');
                        setCustomAuthor('');
                    }}
                />
                <Form.Check.Label htmlFor="author-my">My Work</Form.Check.Label>
                </Form.Check>
                {findPermission('content_scheduler','can_filter_custom_author') &&(
                  <Form.Check type="radio" id="author-custom" name="author">
                  <Form.Check.Input
                      type="radio"
                      name="author"
                      value="custom"
                      checked={authorType === 'user_email'}
                      onChange={() => {
                          setAuthorType('user_email');
                          setCustomAuthor('');
                      }}
                  />
                  <Form.Check.Label htmlFor="author-custom">Custom</Form.Check.Label>
                  </Form.Check>
                )}
            </div>
            </Form.Group>

        {authorType === "user_email" && (
            <Form.Group className="mb-3">
                <Form.Label>Search Author by Name</Form.Label>

                <div style={{ position: 'relative' }}>
                <IoIosSearch className='scheduler_search_icon'
                />

                <Select
                    placeholder="Search author by name"
                    options={authorEmailData}
                    getOptionLabel={(option) => option.user__email}
                    getOptionValue={(option) => option.user_id}
                    value={customAuthor}
                    onChange={(selected) => {setCustomAuthor(selected); setTitle(null)}}
                    onInputChange={(term) => {
                      if(term!="")
                        handleInputChange('user_email', term)
                    }}
                    onFocus={()=>dispatch(getAuthorEmailFilter({filter_type: "user_email"}))}
                    isSearchable
                    isClearable={true}
                    isLoading={authorEmailLoading}
                    filterOption={() => true}
                    styles={{
                      control: (base) => ({
                          ...base,
                          paddingLeft: 30, 
                          backgroundColor: customAuthor ? 'rgba(245, 245, 245, 0.9)' : 'transparent' ,
                          color:'#474747',
                      }),
                      input: (base) => ({
                          ...base,
                          marginLeft: 4,
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 2,
                      }),
                    }}
                />
                </div>
            </Form.Group>
        )}


        <Form.Group className="mb-3">
             <Form.Label>Search by Title</Form.Label>
                <div style={{ position: 'relative' }}>
                <IoIosSearch className='scheduler_search_icon'/>

                <Select
                    placeholder="Search by title"
                    value={title}
                    onChange={(obj) => setTitle(obj)}
                    onInputChange={(term) => {
                      if(term!='') handleInputChange('content_title', term)
                    }}
                    onFocus={handleFocusTitle}
                    options={contentTitleData || []}
                    getOptionLabel={(option) => option.content_title}
                    getOptionValue={(option) => option.id}
                    isClearable={true}
                    isSearchable
                    filterOption={() => true}
                    isLoading={contentTitleLoading}
                    styles={{
                    control: (base) => ({
                        ...base,
                        paddingLeft: 30,
                        backgroundColor: title ? 'rgba(245, 245, 245, 0.9)' : 'transparent' ,
                        color:'#474747',
                    }),
                    input: (base) => ({
                        ...base,
                        marginLeft: 4,
                    }),
                    }}
                />
                </div>
            </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Filter by Entity</Form.Label>
          <Select
                value={entityType}
                onChange={(selected) => {
                    setEntityType(selected);
                    setSelectedEntities([]);
                }}
                options={entityTypeData?.entity_types}
                getOptionLabel={(option) => option?.entity_type}
                getOptionValue={(option) => option?.entity_type}
                placeholder="Search and Select Entity Type"
                isClearable={true}
                isSearchable
            />
        </Form.Group>

        {entityType && (
            <Form.Group className="mb-3">
            <Form.Label>Entity Name</Form.Label>
              <Select
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={entityNameData?.entity_names}
                getOptionLabel={(option) => option.entity_name}
                getOptionValue={(option) => option.article_entity_id}
                value={selectedEntities}
                onChange={(selected) => setSelectedEntities(selected || [])}
                onInputChange={(term)=>{
                  if(term != "") handleInputChange('entity_name',term)
                }}
                components={{
                  Option: customOption,
                  MultiValue: customMultiValue,
                }}
                placeholder="Entity Name"
                filterOption={() => true}
                isSearchable={true}
                styles={customStyles}
              />
          </Form.Group>
        )}

        {message && (
            <div>
              <Alert severity={message && 'error'}>{message}</Alert>
            </div>
         )}

      </Modal.Body>
      <Modal.Footer className="filter_btn_container">
        <Button variant="light" onClick={clearFilters} className="filter_clear_btn">Clear All</Button>
        <Button variant="primary" onClick={applyFilters} className="filter_active_btn">Apply</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilterModal;